import { useCallback, useEffect, useReducer } from 'react';
import { fetcher } from '../../../../../../../../components/Hooks/useListActions';
import { useApplicationContext } from '../../../../../../../context/ApplicationContext/ApplicationContext';
import { ChatParticipant } from '../../../../../../types/chat.types';
import {
  chatParticipantsInitialState,
  chatParticipantsReducer,
  fetchMoreResult,
  fetchingMore,
  insertParticipant,
  removeParticipant,
  updateQuery,
} from './chatParticipants.state';
import { ChatParticipantsManager } from './chatParticipants.types';

const DEFAULT_ITEMS_PER_PAGE = 12;

const useChatParticipants = (): ChatParticipantsManager => {
  const { isStore } = useApplicationContext();
  const [state, dispatch] = useReducer(chatParticipantsReducer, chatParticipantsInitialState);

  const fetchMore = useCallback(
    async (page: number) => {
      dispatch(fetchingMore());
      const response = await fetcher<ChatParticipant>('/chats/participants')({
        count: DEFAULT_ITEMS_PER_PAGE,
        page,
        params: {
          order: { [isStore ? 'brandName' : 'storeName']: 'ASC' },
          name: state.query,
        },
      });
      dispatch(fetchMoreResult(response));
    },
    [isStore, state.query],
  );

  const changeQuery = useCallback((query: string) => {
    dispatch(updateQuery({ query }));
  }, []);

  const reinsertChatParticipant = useCallback((chatParticipant: ChatParticipant) => {
    dispatch(insertParticipant({ participant: chatParticipant }));
  }, []);

  const removeChatParticipant = useCallback((chatParticipant: ChatParticipant) => {
    dispatch(removeParticipant({ participant: chatParticipant }));
  }, []);

  const loadMoreRows = () => {
    if (state.hasMore && !state.isLoading) {
      fetchMore(state.page);
    }
  };

  useEffect(() => {
    fetchMore(0);
  }, [fetchMore, state.query]);

  return {
    state,
    changeQuery,
    reinsertChatParticipant,
    loadMoreRows,
    removeChatParticipant,
  };
};

export default useChatParticipants;
